import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { hapticImpact } from '@/lib/haptic';

const DEFAULT_DELAY_MS = 500;
const DEFAULT_MOVE_TOLERANCE_PX = 12;
const SCROLL_CANCEL_THRESHOLD_PX = 3;

function getScrollTop(element: EventTarget | null): number {
  if (!(element instanceof Element)) {
    return window.scrollY;
  }

  const scrollParent = element.closest('.app-scroll');
  if (scrollParent instanceof HTMLElement) {
    return scrollParent.scrollTop;
  }

  return window.scrollY;
}

export interface LongPressHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerLeave: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
  onContextMenu: (event: ReactMouseEvent) => void;
}

export function useLongPress({
  onLongPress,
  onClick,
  enabled = true,
  delayMs = DEFAULT_DELAY_MS,
  moveTolerancePx = DEFAULT_MOVE_TOLERANCE_PX,
}: {
  onLongPress?: () => void;
  onClick?: () => void;
  enabled?: boolean;
  delayMs?: number;
  moveTolerancePx?: number;
}): {
  isPressing: boolean;
  progress: number;
  handlers: LongPressHandlers;
} {
  const [isPressing, setIsPressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const pressCancelledRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const scrollTopAtStartRef = useRef<number | null>(null);
  const pressStartRef = useRef(0);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      if (progressIntervalRef.current !== null) clearInterval(progressIntervalRef.current);
    },
    [],
  );

  const clearPress = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setIsPressing(false);
    setProgress(0);
    startPosRef.current = null;
    scrollTopAtStartRef.current = null;
  }, []);

  const cancelPress = useCallback(() => {
    pressCancelledRef.current = true;
    clearPress();
  }, [clearPress]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled || event.button !== 0) {
        return;
      }

      longPressTriggeredRef.current = false;
      pressCancelledRef.current = false;
      scrollTopAtStartRef.current = getScrollTop(event.currentTarget);
      startPosRef.current = { x: event.clientX, y: event.clientY };
      pressStartRef.current = Date.now();
      setIsPressing(true);
      setProgress(0);
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - pressStartRef.current;
        setProgress(Math.min(elapsed / delayMs, 1));
      }, 16);

      if (onLongPress) {
        timerRef.current = setTimeout(() => {
          longPressTriggeredRef.current = true;
          clearPress();
          hapticImpact('medium');
          onLongPress();
        }, delayMs);
      }
    },
    [clearPress, delayMs, enabled, onLongPress],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled) {
        return;
      }

      const wasLongPress = longPressTriggeredRef.current;
      const scrollStart = scrollTopAtStartRef.current;
      const scrolled =
        scrollStart !== null &&
        Math.abs(getScrollTop(event.currentTarget) - scrollStart) > SCROLL_CANCEL_THRESHOLD_PX;
      clearPress();

      if (!wasLongPress && !pressCancelledRef.current && !scrolled && onClick) {
        event.preventDefault();
        onClick();
      }
    },
    [clearPress, enabled, onClick],
  );

  const onPointerLeave = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled || !isPressing) {
        return;
      }
      cancelPress();
      event.preventDefault();
    },
    [cancelPress, enabled, isPressing],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const start = startPosRef.current;
      if (!enabled || !start || !isPressing) {
        return;
      }

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.hypot(dx, dy) > moveTolerancePx) {
        cancelPress();
      }
    },
    [cancelPress, enabled, isPressing, moveTolerancePx],
  );

  const onPointerCancel = useCallback(() => {
    cancelPress();
  }, [cancelPress]);

  const onContextMenu = useCallback(
    (event: ReactMouseEvent) => {
      if (enabled && onLongPress) {
        event.preventDefault();
      }
    },
    [enabled, onLongPress],
  );

  return {
    isPressing,
    progress,
    handlers: {
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerMove,
      onPointerCancel,
      onContextMenu,
    },
  };
}
